import { Duration, add, parseISO } from 'date-fns';

import capitalize from 'lodash/capitalize';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';

import { AssayConfig, MonitorType } from '../AssayConfig';

import {
  GetAssaysRequest,
  GetAssayResultsRequest,
  SetActiveRequest,
  Baseline,
  FieldTaggedSummaries,
  Bins,
  SeriesSummaryStatistics,
  BinMode,
  RollingWindow,
  Summarizer,
  Scheduling,
  PGInterval,
  RunFrequency,
  PreviewRequest,
  ScheduleRequest,
  PreviewResult,
  Targeting,
  AssayV2, DataOrigin, AssayResultV2,
} from '../../../openapi';
import {
  AssayDetailsFromV2,
  MappedAssayResultsFromV2,
  MappedPreviewResults,
  RawRunSummary,
} from '../../../types/assay.types';

export function get_list_request(
  pipeline_name: string,
  active: boolean | null | undefined
): GetAssaysRequest {
  return {
    active: active === true ? 'ActiveOnly' : active === false ? 'InactiveOnly' : 'All',
    pipeline_name,
  };
}

export function get_results_request(
  assay_id: string,
  start: string,
  end: string
): GetAssayResultsRequest {
  return {
    id: assay_id,
    start,
    end,
  };
}

export function set_active_request(
  assay_id: string,
  active: boolean
): SetActiveRequest {
  return {
    assay_id,
    active,
  };
}


function get_bin_mode(assayConfig: AssayConfig): BinMode {
  const v1_mode = assayConfig.summarizer.bin_mode;
  const v1_bin_count = assayConfig.summarizer.num_bins;
  const v1_edges = assayConfig.summarizer.provided_edges;

  let v2_mode: BinMode = 'None';
  if (v1_mode === 'Equal') {
    v2_mode = { Equal: v1_bin_count };
  } else if (v1_mode === 'Quantile') {
    v2_mode = { Quantile: v1_bin_count };
  } else if (v1_mode === 'Provided') {
    // @ts-ignore - API not designed with null values in mind
    v2_mode = { Provided: v1_edges };
  }

  return v2_mode;
}

function to_bins(assayConfig: AssayConfig, baselineSummary: RawRunSummary): Bins {
  const v1_edges = baselineSummary.edges;
  if (v1_edges[v1_edges.length - 1] === null) {
    // TODO - Update old fallback logic
    // @ts-ignore - Old logic - Figure out how to reconcile this with TS definitions
    v1_edges[v1_edges.length - 1] = 'INFINITY';
  }
  const v1_labels = baselineSummary.edge_names;

  return {
    edges: v1_edges,
    labels: v1_labels,
    mode: get_bin_mode(assayConfig),
  };
}

function to_iopath_key(assayConfig: AssayConfig) {
  if (!assayConfig.monitored_field || !assayConfig.monitored_column) {
    // Intentional warning
    // eslint-disable-next-line no-console
    console.warn('IO path was requested without field or column specified!', assayConfig.monitored_field, assayConfig.monitored_column);
    return '';
  }

  const INFERENCE_DATA_KEYS_REGEX = /^((in)|(out))\.(?<name>.*)/;
  const type = assayConfig.monitored_type === MonitorType.OUTPUT ? 'out' : 'in';

  const field = assayConfig.monitored_field.match(INFERENCE_DATA_KEYS_REGEX)?.groups?.name;
  const column = assayConfig.monitored_column === 'None' ? null : assayConfig.monitored_column;
  if (!field) {
    // This may be legacy JSON format.
    if (!column) {
      return `${type}.${assayConfig.monitored_field}`;
    }
    return `${type}.${assayConfig.monitored_field}.${column}`;
  }

  if (!column) {
    return `${type}.${field}`;
  }
  return `${type}.${field}.${column}`;
}

function baseline_from_v1(
  assayConfig: AssayConfig,
  baselineSummary: RawRunSummary | FieldTaggedSummaries | null | undefined,
): Baseline {
  if (baselineSummary == null) {
    throw new Error('Baseline summary is not yet available. Please wait for the preview to load.');
  }
  const iopath = to_iopath_key(assayConfig);

  // TODO - should not have to expect a FieldTaggedSummaries structure - figure out the source of this confusion
  const actualSummary = baselineSummary[iopath] ? baselineSummary[iopath] : baselineSummary;

  // @ts-ignore Result of the above issue with FieldTaggedSummaries structures
  const bins: Bins = actualSummary?.bins || to_bins(assayConfig, baselineSummary);
  const stats: SeriesSummaryStatistics = actualSummary?.statistics || {
    count: baselineSummary.count,
    max: baselineSummary.max,
    mean: baselineSummary.mean,
    median: baselineSummary.median,
    min: baselineSummary.min,
    std: baselineSummary.std,
  };

  // @ts-ignore - API not designed to allow null values for name
  const fts: FieldTaggedSummaries = {
    [iopath]: {
      aggregated_values: actualSummary.aggregated_values,
      aggregation: actualSummary.aggregation,
      bins,
      name: assayConfig.name,
      statistics: stats,
      start: actualSummary.start,
      end: actualSummary.end,
    },
  };
  return {
    Summary: {
      ...fts,
    },
  };
}

function static_window_from_v1(assayConfig: AssayConfig) {
  const fixedWindow = assayConfig?.baseline?.calculated?.fixed_window;
  const vector = assayConfig?.baseline?.vector;

  if (vector?.length) {
    return {
      Vector: vector,
    }
  }

  if (fixedWindow) {
    const start = fixedWindow?.start;
    const end = fixedWindow?.end;

    return {
      Static: [start, end]
    }
  }

  // TODO - What do we do here? No Fixed window, but no summary either
  const Summary = assayConfig.baseline?.static;
  return { Summary }
}

function rolling_window_from_v1(assayConfig: AssayConfig): RollingWindow {
  // @ts-ignore API Not designed to expect null width
  const [quantity, unit] = assayConfig.window.width.split(' ');
  const numQuantity = Number(quantity);
  let width = 0;
  switch (unit) {
    case 'minute':
    case 'minutes':
      width = numQuantity * 60;
      break;
    case 'hour':
    case 'hours':
      width = numQuantity * 60 * 60;
      break;
    case 'week':
    case 'weeks':
      width = numQuantity * 86400 * 7;
      break;
    case 'day':
    case 'days':
    default:
      width = numQuantity * 86400;
      break;
  }

  return {
    width: {
      seconds: width,
    },
  };
}

export function summarizer_from_v1(assayConfig: AssayConfig): Summarizer {
  return {
    UnivariateContinuous: {
      bin_mode: get_bin_mode(assayConfig),
      aggregation: assayConfig.summarizer.aggregation,
      metric: assayConfig.summarizer.metric,
      bin_weights: assayConfig.summarizer.bin_weights,
    },
  };
}

function scheduling_from_v1(assayConfig: AssayConfig): Scheduling {
  const interval: string | null = assayConfig.window.interval || assayConfig.window.width;
  // @ts-ignore - API Not designed to expect null interval value
  const [quant, u] = interval.split(' ');
  const quantity = parseInt(quant, 10);
  const us = u[u.length - 1] === 's' ? u.substring(0, u.length - 1) : u;
  const unit = capitalize(us) as 'Day' | 'Minute' | 'Hour' | 'Week';

  const pgInterval: PGInterval = { quantity, unit };
  const run_frequency: RunFrequency = { SimpleRunFrequency: pgInterval };

  return {
    // @ts-ignore - API not designed to accept null/undefined for first_run
    first_run: assayConfig.window.start,
    end: assayConfig.window.end,
    run_frequency,
  };
}

function targeting_from_v1(assayConfig: AssayConfig): Targeting {
  const alert = assayConfig.alert_threshold;
  // TODO: THIS IS DEBUGGING, REMOVE THE FALLBACK:
  const warn = assayConfig.warning_threshold ?? 0.1;

  const {
    pipeline_name,
    pipeline_id,
    workspace_name,
    workspace_id,
  } = assayConfig;
  // @ts-ignore - fallback logic upfront from old code - assayConfig.locations was referenced here, but should not exist
  const locations: string[] | null = assayConfig?.locations || assayConfig?.window?.locations || null;
  const model_id = assayConfig.window.model_name;

  // TODO - Fix numeric conversion to simplify typescript definitions
  const column: string | null = assayConfig?.monitored_column;
  const noIndexProvided: boolean = column === 'None' || column === null;
  const columnAsIndex: number | null = isString(column) ? parseInt(column, 10) : column;
  // @ts-ignore resolving this definition will require changing how we convert the index
  const indexes: null | number[] = noIndexProvided ? null : [columnAsIndex];

  const data_origin: DataOrigin = {
    pipeline_id,
    // @ts-ignore - API Not designed to accept null for pipeline_name
    pipeline_name,
    // @ts-ignore - API Not designed to accept null for workspace_name
    workspace_name,
    workspace_id,
    locations,
    model_id,
  };

  return {
    iopath: [
      {
        // @ts-ignore - API Not designed to accept null for field
        field: assayConfig.monitored_field,
        indexes,
        thresholds: {
          alert,
          warning: warn,
        },
      },
    ],
    data_origin,
  };
}

export function preview_result_from_v1(
  result: PreviewResult,
  assayConfig: AssayConfig,
  baselineSummary: RawRunSummary | FieldTaggedSummaries
): MappedPreviewResults {
  const iopath = to_iopath_key(assayConfig);
  // @ts-ignore - This function has been used in some areas where the score object hasn't been mapped - this is fallback logic for those cases
  let score = isNumber(result?.scores[iopath]) ? result.scores[iopath] : result.scores[iopath]?.score;
  let error: string | null = null;
  let status = (score ?? 0) < assayConfig.alert_threshold ? 'Ok' : 'Alert';

  // TODO - Determine how to handle this case
  if (typeof score === 'string') {
    error = score;
    score = 0;
    status = 'Alert';
  }

  const id = result.id === undefined ? null : result.id;
  const assay_id = result.assay_id === undefined ? null : result.assay_id;
  const pipeline_id = result.pipeline_id === undefined ? null : result.pipeline_id;
  const bin_index = score?.bin_index === undefined ? null : result.pipeline_id;
  const created_at = result?.analyzed_at === undefined ? null : result.analyzed_at;

  const windowSummary = result.summaries[iopath];
  const actualBaselineSummary = baselineSummary[iopath] ? baselineSummary[iopath] : baselineSummary;

  return {
    id,
    assay_id,
    pipeline_id,
    bin_index,
    created_at,
    // TODO: Go through dashboard and clean this up.
    // v1 uses the window_start, but really each plot should use the window_end
    window_start: result?.window_end,
    analyzed_at: result?.window_end,
    elapsed_millis: isNumber(result?.elapsed_millis) ? result.elapsed_millis : 0,
    baseline_summary: actualBaselineSummary,
    window_summary: windowSummary,
    warning_threshold: assayConfig.warning_threshold,
    alert_threshold: assayConfig.alert_threshold,
    score,
    error,
    scores: windowSummary?.aggregated_values,
    summarizer: assayConfig.summarizer,
    status,
  };
}

export function true_result_from_v1(
  result: AssayResultV2,
  assayConfig: AssayConfig
): MappedAssayResultsFromV2 {
  const iopath = Object.keys(result.scores ?? {})[0];
  const rawScore = result.scores?.[iopath];
  let numScore;
  let binIndex: number | null = null;
  if (typeof rawScore === 'object') {
    numScore = rawScore?.score || 0;
    binIndex = rawScore?.bin_index === undefined ? null : rawScore.bin_index;
  } else {
    numScore = rawScore || 0;
  }
  // @ts-ignore alertThreshold reference was the old reference, but we should actually be using the underscored version
  const alert_threshold = isNumber(assayConfig?.alert_threshold) ? assayConfig.alert_threshold : assayConfig?.alertThreshold
  // @ts-ignore fallback logic aligned with the above line
  const warning_threshold = isNumber(assayConfig?.warning_threshold) ? assayConfig.warning_threshold : assayConfig?.warningThreshold
  let status = (numScore ?? 0) < alert_threshold ? 'Ok' : 'Alert';
  if (typeof rawScore === 'string') {
    numScore = 0;
    status = 'Alert';
  }

  return {
    id: `${result.window_start.toString()}-id`,
    assay_id: result.assay_id,
    // TODO: Go through dashboard and clean this up.
    // v1 uses the window_start, but really each plot should use the window_end
    window_start: result.window_end,
    analyzed_at: result.updated_at,
    elapsed_millis: isNumber(result?.elapsed_millis) ? result.elapsed_millis : 0,
    pipeline_id: result?.pipeline_id === undefined ? null : result.pipeline_id,
    baseline_summary: assayConfig.baseline[iopath],
    window_summary: result.summaries?.[iopath],
    warning_threshold,
    alert_threshold,
    score: numScore,
    scores: result.summaries?.[iopath]?.aggregated_values,
    bin_index: binIndex,
    summarizer: assayConfig.summarizer,
    status,
    created_at: result.created_at,
  };
}
// TODO - Incorporate logic for Vector
export function get_preview_baseline_binning_request(assayConfig: AssayConfig) {
  return {
    baseline: static_window_from_v1(assayConfig),
    summarizer: summarizer_from_v1(assayConfig),
    targeting: targeting_from_v1(assayConfig),
  }
}

export function get_preview_request(
  assayConfig: AssayConfig,
  baselineSummary: RawRunSummary | FieldTaggedSummaries,
  preview_start: string,
  preview_end: string
): PreviewRequest {
  return {
    preview_end,
    preview_start,
    baseline: baseline_from_v1(assayConfig, baselineSummary),
    scheduling: scheduling_from_v1(assayConfig),
    summarizer: summarizer_from_v1(assayConfig),
    targeting: targeting_from_v1(assayConfig),
    window: rolling_window_from_v1(assayConfig),
  };
}

export function get_schedule_request(
  assayConfig: AssayConfig,
  baselineSummary: RawRunSummary
): ScheduleRequest {
  return {
    // @ts-ignore - API not designed to accept null for name
    name: assayConfig.name,
    baseline: baseline_from_v1(assayConfig, baselineSummary),
    scheduling: scheduling_from_v1(assayConfig),
    summarizer: summarizer_from_v1(assayConfig),
    targeting: targeting_from_v1(assayConfig),
    window: rolling_window_from_v1(assayConfig),
  };
}

/**
 * @deprecated Assays V1 Logic
 */
export function calculate_next_run(lastRun: string, duration: Duration) {
  return add(parseISO(lastRun), duration);
}

export function get_bins(mode: BinMode) {
  if (mode === 'None') {
    return ['None', 0];
  }

  const key = Object.keys(mode)[0];
  // @ts-ignore - This is a discriminated union, the key is guaranteed to exist.
  return [key, mode[key]];
}

// FIXME - These typescript definitions are having issues with the & operator - they exist, but TS is still flagging them
// Updating to avoid errors will require reorganizing the source code slightly
export const mapAssayV2Details = (assay: AssayV2): AssayDetailsFromV2 => {
  const ioPathIndeces = assay.targeting.iopath[0]?.indexes || 'None';
  const iopath = `${assay.targeting.iopath[0].field}.${ioPathIndeces}`;

  // @ts-ignore - Typescript compiler having issues with | operator
  const baselineStart = assay.baseline.Summary?.[iopath]?.start;
  // @ts-ignore - Typescript compiler having issues with | operator
  const baselineEnd = assay.baseline.Summary?.[iopath]?.start;

  // @ts-ignore - Typescript compiler having issues with | operator
  const { unit, quantity } = assay.scheduling.run_frequency.SimpleRunFrequency!;
  const durationUnit = unit.toLowerCase();
  const duration = { [`${durationUnit}s`]: quantity };
  // @ts-ignore - Typescript compiler having issues with | operator
  const bins = get_bins(assay.summarizer.UnivariateContinuous?.bin_mode!);

  const locations: string[] | null = assay?.targeting?.data_origin?.locations || null;
  const pipeline_id: number = assay?.targeting?.data_origin?.pipeline_id || 0;

  return {
    id: assay.id,
    name: assay.name,
    iopath,
    locations,
    active: assay.active,
    // This is essentially the same as active.
    status: isBoolean(assay.active) ? assay.active : true,
    alertThreshold: assay.targeting.iopath[0]?.thresholds?.alert,
    pipeline_id,
    pipelineID: pipeline_id,
    pipelineName: assay.targeting.data_origin.pipeline_name,
    // TODO: We can calculate this from results.
    lastRun: null,
    lastWindowStart: null,
    // @ts-ignore - Typescript compiler throwing false alarm because of | operator
    baseline: assay.baseline.Summary,
    baselineStart,
    baselineEnd,
    // modelName: assay.baseline?.calculated?.fixed_window?.model_name,
    runUntil: assay.scheduling.end,
    created: assay.created_at,
    updatedAt: assay.updated_at,
    summarizerMetadata: {
      binMode: bins[0],
      numBins: bins[1],
      // @ts-ignore - Typescript compiler throwing false alarm because of | operator
      ...assay.summarizer.UnivariateContinuous,
    },
    window: assay.window,
    // This is a date-fns Duration.
    runFrequencyDuration: duration,
  };
};
